<template>
  <div>

    <template
      v-if="gvnt !== null"
    >
      <table
        class="prop-table mb-4"
      >
        <tr>
          <th>Type</th>
          <td>{{gvnt.type | getGvntTypeName}}</td>
        </tr>
        <tr>
          <th>
            Status
          </th>
          <td
            :class="statusClass"
          >
            {{gvnt.status | getGvntStatusName}}
          </td>
        </tr>
        <tr>
          <th>Client min version</th>
          <td>{{gvnt.client_min_version | convertOptionalVersionCodeToStr('not specified')}}</td>
        </tr>
        <tr>
          <th>Created at</th>
          <td>{{gvnt.ctime | getMomentStr}}</td>
        </tr>
        <tr>
          <th>Planned start</th>
          <td>{{gvnt.stime | getMomentStr}}</td>
        </tr>
        <tr>
          <th>Real start</th>
          <td>{{gvnt.srtime | getGvntRealStartTimeStr}}</td>
        </tr>
        <tr>
          <th>Remaining time</th>
          <td>{{remainingTimeStr}}</td>
        </tr>
        <tr>
          <th>Planned finish</th>
          <td>{{gvnt.ftime | getMomentStr}}</td>
        </tr>
        <tr>
          <th>Real finish</th>
          <td>{{gvnt.frtime | getGvntRealFinishTimeStr}}</td>
        </tr>
        <tr>
          <th>Planned duration</th>
          <td>{{expectedDurationStr}}</td>
        </tr>
        <tr>
          <th>Real duration</th>
          <td>{{realDurationStr}}</td>
        </tr>
      </table>

      <div>
        <b-button
          v-if="isEditButtonVisible"
          :to="{name: 'gvnt_edit'}"
          variant="primary"
        >
          Edit
        </b-button>

        <b-button
          v-if="mustShowForceStartButton"
          @click="forceStartGvntWithConfirmation()"
        >
          Force start
        </b-button> 
      </div>
    </template>

    <p v-if="message !== null">
      {{message}}
    </p>

    <loading-indicator
      v-if="isLoading"
    />

  </div>
</template>

<script>
import auth from '../auth.js';
import notifier from '../notifier.js';
import { HTTP } from '../http-common.js';

/**
 * @typedef { import('../model/gvnt.js').Gvnt } Gvnt
 */

import { GVNT_STATUS_INCOMING      } from '../model/gvnt.js';
import { GVNT_STATUS_ACTIVE        } from '../model/gvnt.js';
import { getGvntTypeName           } from '../model/gvnt.js';
import { getGvntStatusName         } from '../model/gvnt.js';
import { getGvntStatusClass        } from '../model/gvnt.js';
import { getGvntRealStartTimeStr   } from '../model/gvnt.js';
import { getGvntRealFinishTimeStr  } from '../model/gvnt.js';
import { getGvntRemainingTimeStr   } from '../model/gvnt.js';
import { getGvntPlannedDurationStr } from '../model/gvnt.js';
import { getGvntRealDurationStr    } from '../model/gvnt.js';
import { mayManageGvnt             } from '../model/gvnt.js';
import { canEditGvnt               } from '../model/gvnt.js';

/**
 * @typedef { import('../service/confirm.js').ConfirmHandle } ConfirmHandle
 */
import { confirmAction } from '../service/confirm.js';

import LoadingIndicator from './LoadingIndicator.vue';

import NowSecMixin from '../NowSecMixin.js';

const LOAD_STATE_INIT          = 1;
const LOAD_STATE_LOADING       = 2;
const LOAD_STATE_LOADED_NORMAL = 3;
const LOAD_STATE_LOADED_ERROR  = 4;

export default {
  name: 'GvntInfo',

  components: {
    LoadingIndicator,
  },

  mixins: [
    NowSecMixin,
  ],

  props: {
    gvntId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      actor: auth.getViewer(),

      loadState: LOAD_STATE_INIT,

      /** @type {?Gvnt} */
      gvnt: null,
    };
  },

  computed: {
    /** @returns {boolean} */
    isLoading() {
      return this.loadState === LOAD_STATE_LOADING;
    },

    /** @returns {boolean} */
    isLoadedNormal() {
      return this.loadState === LOAD_STATE_LOADED_NORMAL;
    },

    /** @returns {?string} */
    message() {
      if(this.isLoadedNormal && this.gvnt === null) {
        return `Game event with id ${this.gvntId} does not exist.`;
      }
      return null;
    },

    /** @returns {?Object} */
    statusClass() {
      const gvnt = this.gvnt;
      if(gvnt === null) {
        return null;
      }

      return getGvntStatusClass(gvnt.status);
    },

    /** @returns {?string} */
    remainingTimeStr() {
      const gvnt = this.gvnt;
      if(gvnt === null) {
        return null;
      }

      return getGvntRemainingTimeStr(gvnt, this.nowSec);
    },

    /** @returns {string} */
    expectedDurationStr() {
      const gvnt = this.gvnt;
      if(gvnt === null) {
        return '?';
      }

      return getGvntPlannedDurationStr(gvnt);
    },

    /** @returns {string} */
    realDurationStr() {
      const gvnt = this.gvnt;
      if(gvnt === null) {
        return '?';
      }

      return getGvntRealDurationStr(gvnt);
    },

    /** @returns {boolean} */
    isEditButtonVisible() {
      const actor = this.actor;
      if(actor === null) {
        return false;
      }

      const gvnt = this.gvnt;
      if(gvnt === null) {
        return false; // data not loaded
      }

      return canEditGvnt(actor.role, gvnt.status);
    },

    /** @returns {boolean} */
    mustShowForceStartButton() {
      const actor = this.actor;
      if(actor === null) {
        return false;
      }

      if(!mayManageGvnt(actor.role)) {
        return false;
      }
      if(this.gvnt === null) {
        return false; // data not loaded
      }
      return this.gvnt.status === GVNT_STATUS_INCOMING;
    },
  },

  created() {
    this.loadGvnt();
  },

  methods: {
    loadGvnt() {
      notifier.deleteSenderNotifications(this);

      this.loadState = LOAD_STATE_LOADING;

      const url = `/gvnt/${this.gvntId}/info`;
      HTTP.get(url)
      .then(response => {
        this.gvnt = response.data.data;
        this.loadState = LOAD_STATE_LOADED_NORMAL;
      })
      .catch(err => {
        this.loadState = LOAD_STATE_LOADED_ERROR;
        notifier.showError(err, {sender: this});
      });
    },

    forceStartGvntWithConfirmation() {
      this.confirmForceStart(() => {
        this.forceStart();
      });
    },

    /**
     * @param {ConfirmHandle} onConfirmed
     */
    confirmForceStart(onConfirmed) {
      confirmAction(
        this,
        'Are you sure you want to force start this game event {env}?',
        'Force start',
        onConfirmed,
      );
    },

    forceStart() {
      notifier.deleteSenderNotifications(this);

      const gvnt = this.gvnt;
      if(gvnt === null) {
        notifier.showError('Game event not loaded.', {sender: this});
        return;
      }

      const url = `/gvnt/${gvnt.id}/force_start`;
      HTTP.post(url)
      .then(response => {
        const data = response.data.data;
        if(data === null) {
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        gvnt.srtime = data.srtime;
        gvnt.status = GVNT_STATUS_ACTIVE;
        notifier.showSuccess('Game event started.');
      })
      .catch(e => {
        notifier.showError(e.response.data, {sender: this});
      });
    },
  },

  filters: {
    getGvntTypeName,
    getGvntStatusName,
    getGvntRealStartTimeStr,
    getGvntRealFinishTimeStr,
  },
};
</script>
